import React, { useEffect, useRef, useState } from 'react'
import Button from '@material-ui/core/Button'
import CircularProgress from '@material-ui/core/CircularProgress'
import Dialog from '@material-ui/core/Dialog'
import DialogActions from '@material-ui/core/DialogActions'
import DialogContent from '@material-ui/core/DialogContent'
import DialogContentText from '@material-ui/core/DialogContentText'
import DialogTitle from '@material-ui/core/DialogTitle'

import { queueDialogFocusRestoration } from './accessibility'
import { normalizeSectionError } from './sectionState'

let dialogSequence = 0
const nextDialogId = () => {
    dialogSequence += 1
    return `appointment-settings-destructive-dialog-${dialogSequence}`
}

const failedResult = result => {
    if (Array.isArray(result) && result[0]) return result[0]
    if (result && result.ok === false) return result.error || result
    return null
}

export const DestructiveConfirmationDialog = ({
    open,
    title,
    recordLabel,
    consequence,
    confirmLabel,
    cancelLabel = 'Cancel',
    onConfirm,
    onClose,
    onConfirmed,
    openerRef,
    sectionHeadingRef,
}) => {
    const id = useRef(nextDialogId()).current
    const cancelRef = useRef(null)
    const pendingRef = useRef(false)
    const [pending, setPending] = useState(false)
    const [failure, setFailure] = useState(null)
    const titleId = `${id}-title`
    const descriptionId = `${id}-description`
    const errorId = `${id}-error`

    useEffect(() => {
        if (!open) return
        pendingRef.current = false
        setPending(false)
        setFailure(null)
    }, [open, recordLabel])

    const closeAndRestore = reason => {
        if (pendingRef.current) return
        if (onClose) onClose(reason)
        queueDialogFocusRestoration(openerRef, sectionHeadingRef)
    }

    const handleConfirm = async () => {
        if (pendingRef.current) return
        pendingRef.current = true
        setPending(true)
        setFailure(null)
        let result
        try {
            result = await onConfirm()
            const resultFailure = failedResult(result)
            if (resultFailure) throw resultFailure
        } catch (error) {
            pendingRef.current = false
            setPending(false)
            setFailure(normalizeSectionError(error, 'The change could not be completed. Please try again.'))
            return
        }

        pendingRef.current = false
        setPending(false)
        if (onConfirmed) {
            try {
                await onConfirmed(result)
            } catch {
                // The destructive mutation already committed; callback failures must not make it retryable.
            }
        }
        closeAndRestore('confirmed')
    }

    const handleDialogClose = () => closeAndRestore('cancelled')

    return (
        <Dialog
            open={open}
            onClose={handleDialogClose}
            disableBackdropClick
            disableEscapeKeyDown={pending}
            disableRestoreFocus
            aria-labelledby={titleId}
            aria-describedby={[descriptionId, failure ? errorId : null].filter(Boolean).join(' ')}
            TransitionProps={{
                onEntered: () => {
                    if (cancelRef.current) cancelRef.current.focus()
                },
            }}
        >
            <DialogTitle id={titleId}>{title}</DialogTitle>
            <DialogContent>
                <DialogContentText id={descriptionId}>
                    <strong>{recordLabel}</strong>
                    {' — '}
                    {consequence}
                </DialogContentText>
                {!!failure && (
                    <div id={errorId} role="alert">
                        {`Error: ${failure.message}`}
                    </div>
                )}
            </DialogContent>
            <DialogActions>
                <Button ref={cancelRef} type="button" onClick={() => closeAndRestore('cancelled')} disabled={pending}>
                    {cancelLabel}
                </Button>
                <Button
                    type="button"
                    variant="contained"
                    color="secondary"
                    onClick={handleConfirm}
                    disabled={pending}
                    aria-describedby={descriptionId}
                >
                    {confirmLabel}
                    {pending && <CircularProgress size={16} color="inherit" aria-hidden="true" />}
                </Button>
            </DialogActions>
        </Dialog>
    )
}
